// Schermate dell'app: avvio, errore, attesa dell'approvazione, agenda del giorno.
import { doc, onSnapshot } from 'firebase/firestore';
import { t, lingua, scegliLingua, NOMI_LINGUE } from '../i18n.js';
import { h, sostituisci, app, avviso, chiudiFoglio } from './dom.js';
import { oggiRoma, spostaGiorno, giornoLungo } from './date.js';
import { inviaRichiesta, codiceTelefono, ascoltaRichieste, approva, rifiuta, RUOLI } from '../dati/accesso.js';
import { ascoltaGiornoIntero, creaPrenotazioni } from '../dati/prenotazioni.js';
import {
  SALE, LETTINI, OPERATRICI, START, END, RIORDINO, colonneDelGiorno, toMin, hm, fine, nomeOperatrice, occupa, spaziLiberi,
  blocchiDel, conflittiDelGiorno, eur, incasso, valoreCompl, noShowDaDecidere, inTurno, adessoRoma,
} from '../logica/agenda.js';
import { TRATTAMENTI } from '../logica/listino.js';
import { admin as eAdmin, puoPrenotare } from '../logica/ruoli.js';
import { apriModulo, apriScheda, testoStato } from './prenotazione.js';
import { apriTurni, testoTurno } from './turni.js';

// «SENSORIAL · sezione» in piccolo, come in tutte le schermate della tela D9; a destra un pulsante (facoltativo).
export const testata = (sezione, ...destra) => h('header', { classe: 'testata' },
  h('span', { classe: 'marchio' }, h('b', {}, 'Sensorial'), ` · ${sezione}`), destra);

export function schermataAvvio() {
  sostituisci(app(), h('main', { classe: 'centro' }, testata(t('app.sottotitolo')), h('h1', {}, t('app.nome')), h('p', {}, t('avvio.carico'))));
}

export function schermataErrore(_e, riprova) {
  sostituisci(app(), h('main', { classe: 'centro' }, testata(t('app.sottotitolo')), h('h1', {}, t('app.nome')),
    h('p', {}, t('avvio.errore')),
    h('button', { classe: 'grande', onclick: riprova }, t('avvio.riprova'))));
}

function sceltaLingua(cambia) {
  const sel = h('select', { id: 'lingua', 'aria-label': t('attesa.lingua'), onchange: () => cambia(sel.value) },
    Object.entries(NOMI_LINGUE).map(([c, n]) => h('option', { value: c, selected: c === lingua() }, n)));
  return sel;
}

// Telefono nuovo (o disattivato): nome + richiesta di accesso, con il codice da leggere alla responsabile.
export function schermataAttesa(ctx, { disattivo = false, inviata = false } = {}) {
  const ridisegna = (opz) => schermataAttesa(ctx, { disattivo, inviata, ...opz });
  const nome = h('input', { id: 'nome', maxlength: 40, autocomplete: 'name', placeholder: t('attesa.nome'), 'aria-label': t('attesa.nome') });
  const invia = h('button', {
    classe: 'grande', id: 'chiedi',
    onclick: async () => {
      if (!nome.value.trim()) { nome.focus(); return; }
      invia.disabled = true;
      try {
        // Qui si aspetta la conferma del server (S1): «inviata» solo se la richiesta è arrivata davvero.
        await inviaRichiesta(ctx, { nome: nome.value, lingua: lingua() });
        ridisegna({ inviata: true });
      } catch (e) {
        invia.disabled = false;
        ctx.scatola.registra(e, { dove: 'richiesta' });
        avviso(t('invii.errore', { codice: e.code || '?' }));
      }
    },
  }, t('attesa.invia'));
  sostituisci(app(), h('main', { classe: 'centro' }, testata(t('app.sottotitolo')),
    h('h1', {}, t('attesa.titolo')),
    disattivo ? h('p', {}, t('attesa.disattivo')) : inviata ? h('p', { id: 'inviata' }, t('attesa.inviata')) : [
      h('p', {}, t('attesa.spiega')),
      nome,
      sceltaLingua((c) => { try { localStorage.setItem('spa.lingua', scegliLingua(c)); } catch { /* niente memoria */ } ridisegna(); }),
      invia,
    ],
    h('p', { classe: 'codice' }, t('attesa.codice', { codice: codiceTelefono(ctx.uid) }))));
  return () => {};
}

// Telefoni in attesa: la responsabile sceglie i ruoli (e la colonna dell'operatrice) e approva.
export function pannelloRichieste(ctx, contenitore) {
  return ascoltaRichieste(ctx, (lista) => {
    sostituisci(contenitore, lista.length ? h('section', { classe: 'richieste' },
      h('h2', {}, t('richieste.titolo')),
      lista.map((r) => {
        const scelte = RUOLI.map((ruolo) => h('label', { classe: 'spunta' },
          h('input', { type: 'checkbox', value: ruolo, checked: ruolo === 'operatrice' }), t(`ruolo.${ruolo}`)));
        const op = h('select', { 'aria-label': t('richieste.colonna') },
          h('option', { value: '' }, t('richieste.nessuna')),
          OPERATRICI.map((o) => h('option', { value: o }, nomeOperatrice(o))));
        return h('article', { classe: 'richiesta', dati: { uid: r.uid } },
          h('strong', {}, r.nome), h('small', {}, ` · ${r.codice || ''}`),
          h('div', { classe: 'ruoli' }, scelte),
          h('label', {}, `${t('richieste.colonna')} `, op),
          h('div', { classe: 'azioni' },
            h('button', {
              classe: 'primario',
              onclick: () => {
                const ruoli = scelte.map((l) => l.querySelector('input')).filter((i) => i.checked).map((i) => i.value);
                if (!ruoli.length) { avviso(t('richieste.scegliRuolo')); return; }
                approva(ctx, r, ruoli, op.value || null).catch((e) => {
                  ctx.scatola.registra(e, { dove: 'approva' });
                  avviso(t('invii.errore', { codice: e.code || '?' }));
                });
              },
            }, t('richieste.approva')),
            h('button', { onclick: () => rifiuta(ctx, r).catch((e) => ctx.scatola.registra(e, { dove: 'rifiuta' })) }, t('richieste.rifiuta'))));
      })) : null);
  }, (e) => ctx.scatola.registra(e, { dove: 'richieste' }));
}

// Agenda del giorno come la tela D9: ore a sinistra, sala sopra i suoi 2 lettini (6 colonne), 88 px per ora.
// Aggiornata in tempo reale dal database. Chi prenota tocca uno spazio libero (ora e colonna già pronte) o «+ Prenota»;
// tutti toccano una prenotazione per la scheda.
const PX = 88 / 60; // pixel per minuto (in stile.css: --ora)
const ALTO = (END - START) * PX;
const y = (m) => (m - START) * PX;
const posto = (s, e) => `top:${y(s) + 1}px;height:${Math.max(18, (e - s) * PX - 3)}px`;
const sovrapposte = (a, b) => a[0] < b[1] && b[0] < a[1];

function leggiFiltro() {
  try { return localStorage.getItem('spa.filtroOp') || 'tutte'; } catch { return 'tutte'; }
}

/** @param {{ giorno?: string }} [apri] giorno da mostrare (dalla schermata «Oggi»), sennò oggi */
export function schermataAgenda(ctx, dispositivo, apri = {}) {
  const ruoli = dispositivo.ruoli || [];
  const admin = eAdmin(ruoli);
  const prenota = puoPrenotare(ruoli);
  const scritture = creaPrenotazioni({ ...ctx, nome: dispositivo.nome || '' });
  let giorno = apri.giorno || oggiRoma();
  let ultimo = null; // dati del giorno: { data, prenotazioni, turni, pause, blocchi }
  let nomi = {};
  let filtro = leggiFiltro();
  let stopGiorno = () => {};
  const titolo = h('h1', { id: 'giorno' });
  const stato = h('p', { classe: 'stato-rete', 'aria-live': 'polite' });
  const riepilogo = h('div', { classe: 'riepilogo', id: 'riepilogo' });
  const turni = h('section', { classe: 'turni', id: 'turni', 'aria-label': t('turni.titolo') });
  const scelte = h('div', { classe: 'scelte', role: 'group', 'aria-label': t('agenda.filtro') });
  const griglia = h('div', { classe: 'ag-scorre', id: 'agenda' });
  const richieste = h('div');

  const vista = {
    ctx, dispositivo, ruoli, scritture,
    data: () => giorno,
    dati: () => ultimo,
    nomi: () => nomi,
    vaiA: (k) => apriGiorno(k),
  };

  // Nome corto per le colonne strette (filtro e riquadri): il nome scritto dalla responsabile o «Op. N».
  const breve = (o) => nomi[o] || t('op.breve', { n: OPERATRICI.indexOf(o) + 1 });

  function blocco(b, conProblemi, stile) {
    const tr = TRATTAMENTI[b.tr];
    const compagno = b.coppia ? ultimo.prenotazioni.find((o) => o.coppia === b.coppia && o.id !== b.id) : null;
    const mini = b.coppia ? [t('agenda.coppia'), compagno ? t('agenda.conLettino', { n: compagno.lettino }) : null].filter(Boolean).join(' · ')
      : b.tipo !== 'pagamento' ? t(`tag.${b.tipo}`) : '';
    const fatto = b.stato === 'arrivato' || b.stato === 'fatto';
    const attiva = occupa(b);
    // Sotto i 75' ci stanno 3 righe: l'etichetta va accanto all'ora e il prezzo si vede nella scheda.
    const corto = b.durata < 75;
    return h('button', {
      type: 'button',
      classe: `ag-b pren tipo-${b.tipo} stato-${b.stato}${attiva ? '' : ' spenta'}${conProblemi ? ' prob' : ''}${b.inSospeso ? ' sospeso' : ''}`,
      dati: { id: b.id, op: b.op },
      style: stile,
      onclick: () => apriScheda(vista, b.id),
    },
    h('span', { classe: 't' }, fatto ? '✓ ' : '', `${hm(toMin(b.inizio))}–${hm(fine(b))}`, attiva && corto && mini ? ` · ${mini}` : ''),
    h('span', { classe: 'n' }, b.camera ? `${b.camera} · ` : '', b.nome || t('agenda.ospite')),
    // Disdetta e no-show: riquadro stretto a destra, solo ora, nome e stato.
    attiva ? h('span', { classe: 'd' }, breve(b.op), ' · ', tr?.nome || b.tr) : null,
    attiva ? null : h('span', { classe: 'd' }, testoStato(b)),
    b.inSospeso ? h('em', { classe: 'd' }, t('agenda.inSospeso')) : null,
    attiva && !corto && mini ? h('span', { classe: `mini${b.tipo === 'voucher' || b.tipo === 'complimentary' ? ' oro' : ''}` }, mini) : null,
    attiva && !corto && b.tipo === 'pagamento' && b.importo ? h('span', { classe: 't prezzo' }, eur(b.importo)) : null);
  }

  // Una colonna (sala × lettino): prenotazioni, riordino di 15', spazi liberi, fuori servizio (S8).
  function colonna(s, l, lista, conflitti) {
    const attive = lista.filter(occupa);
    const spente = lista.filter((b) => !occupa(b)).map((b) => [toMin(b.inizio), fine(b)]);
    // Due prenotazioni sovrapposte (solo per errore, già segnate in rosso): metà larghezza ciascuna.
    const corsie = [];
    const corsia = new Map();
    for (const b of attive) {
      let i = corsie.findIndex((f) => f <= toMin(b.inizio));
      if (i < 0) { i = corsie.length; corsie.push(0); }
      corsie[i] = fine(b);
      corsia.set(b.id, i);
    }
    const doppia = (b) => attive.some((o) => o !== b && sovrapposte([toMin(o.inizio), fine(o)], [toMin(b.inizio), fine(b)]));
    const voci = [];
    for (const b of lista) {
      const lato = occupa(b) && doppia(b) ? (corsia.get(b.id) ? ';left:50%' : ';right:50%') : '';
      voci.push(blocco(b, conflitti.has(b.id), posto(toMin(b.inizio), fine(b)) + lato));
      if (occupa(b) && fine(b) < END) {
        voci.push(h('div', { classe: 'riordino', style: `top:${y(fine(b)) + 1}px` }, t('agenda.riordino', { n: RIORDINO })));
      }
    }
    // Spazi liberi da toccare solo per chi prenota; i fuori servizio si vedono da tutti.
    if (prenota) {
      for (const x of spaziLiberi(ultimo, s.id, l)) {
        const a = toMin(x.inizio);
        const e = toMin(x.fine);
        voci.push(h('button', {
          type: 'button',
          classe: `ag-b libero${spente.some((z) => sovrapposte(z, [a, e])) ? ' stretto' : ''}`,
          dati: { ora: x.inizio },
          style: posto(a, e),
          onclick: () => apriModulo(vista, { data: giorno, inizio: x.inizio, sala: s.id, lettino: l }),
        }, h('span', { classe: 'n' }, `+ ${hm(a)}`), h('span', { classe: 'd' }, t('agenda.liberoFino', { ora: hm(e) }))));
      }
    }
    for (const x of blocchiDel(ultimo, null, s.id).filter((z) => !+z.lettino || +z.lettino === l)) {
      voci.push(h('div', { classe: 'ag-b fuori', style: posto(toMin(x.inizio), toMin(x.fine)) },
        h('span', { classe: 'n' }, t('agenda.fuoriServizio')),
        h('span', { classe: 't' }, `${hm(toMin(x.inizio))}–${hm(toMin(x.fine))}`),
        x.motivo ? h('span', { classe: 'd' }, x.motivo) : null));
    }
    return h('section', { classe: `colonna ag-col l${l}`, dati: { colonna: `${s.id}-${l}` } },
      h('h2', { classe: 'nascosto' }, `${s.nome} · ${t('agenda.lettino', { n: l })}`), voci);
  }

  // Linea rossa dell'ora attuale (solo oggi, dentro l'orario della SPA).
  function adesso() {
    const { data, minuti } = adessoRoma();
    return data === giorno && minuti >= START && minuti <= END ? minuti : null;
  }

  function disegnaRiepilogo() {
    const pr = ultimo.prenotazioni;
    const n = pr.filter(occupa).length;
    const soldi = pr.reduce((x, b) => x + incasso(b), 0);
    const compl = pr.reduce((x, b) => x + valoreCompl(b), 0);
    const daDecidere = admin ? pr.filter(noShowDaDecidere) : [];
    sostituisci(riepilogo,
      h('span', {}, h('b', {}, n === 1 ? t('agenda.tratt1') : t('agenda.trattN', { n })), ' · ', h('b', {}, eur(soldi)),
        compl ? ` · ${t('agenda.compl', { v: eur(compl) })}` : null),
      daDecidere.length ? h('button', {
        type: 'button', classe: 'et et-oro', id: 'da-decidere', onclick: () => apriScheda(vista, daDecidere[0].id),
      }, t('agenda.noShowDecidere', { n: daDecidere.length })) : null);
  }

  function disegnaFiltro() {
    const voci = [['tutte', t('agenda.tutte')], ...OPERATRICI.map((o) => [o, breve(o)])];
    sostituisci(scelte, voci.map(([k, nome]) => h('button', {
      type: 'button', 'aria-pressed': String(filtro === k), dati: { filtro: k },
      onclick: () => {
        filtro = k;
        try { localStorage.setItem('spa.filtroOp', k); } catch { /* niente memoria */ }
        disegnaFiltro();
        griglia.querySelector('.ag')?.setAttribute('data-filtro', k);
      },
    }, nome)));
  }

  function disegna() {
    titolo.textContent = giornoLungo(giorno);
    if (!ultimo) return;
    disegnaRiepilogo();
    disegnaFiltro();
    sostituisci(turni, OPERATRICI.map((o) => h('div', { classe: `turno-op${inTurno(ultimo, o) ? '' : ' fuori-turno'}`, dati: { op: o } },
      h('strong', {}, nomeOperatrice(o, nomi)), h('small', {}, testoTurno(ultimo, o)))),
    admin ? h('button', { type: 'button', id: 'apri-turni', onclick: () => apriTurni(vista) }, t('turni.apri')) : null);
    const col = colonneDelGiorno(ultimo.prenotazioni);
    const conflitti = new Set(conflittiDelGiorno(ultimo, { nomi }).map((x) => x.prenotazione.id));
    const ore = [];
    for (let m = START; m <= END; m += 30) {
      const alto = m === START ? 2 : m === END ? y(m) - 14 : y(m) - 6;
      ore.push(h(m % 60 ? 'span' : 'b', { style: `top:${alto}px` }, hm(m)));
    }
    const ora = adesso();
    if (ora !== null) ore.push(h('span', { classe: 'adesso', style: `top:${Math.max(2, y(ora) - 6)}px` }, hm(ora)));
    sostituisci(griglia, h('div', { classe: 'ag', style: `--alto:${ALTO}px`, dati: { filtro } },
      h('div', { classe: 'ag-testa' },
        h('div', { classe: 'ag-angolo' }),
        SALE.map((s) => h('div', { classe: `ag-sala sala-${s.id}` }, h('span', { classe: 'pallino', 'aria-hidden': 'true' }), s.nome)),
        h('div', { classe: 'ag-angolo' }),
        SALE.map((s) => LETTINI.map((l) => h('div', { classe: `ag-let l${l} sala-${s.id}` }, t('agenda.lettino', { n: l }))))),
      h('div', { classe: 'ag-corpo' },
        h('div', { classe: 'ag-ore', 'aria-hidden': 'true' }, ore),
        SALE.map((s) => LETTINI.map((l) => colonna(s, l, col[`${s.id}-${l}`], conflitti))),
        ora !== null ? h('div', { classe: 'ag-adesso', 'aria-hidden': 'true', style: `top:${y(ora)}px` }) : null)));
  }

  function apriGiorno(k) {
    giorno = k;
    ultimo = null;
    stopGiorno();
    titolo.textContent = giornoLungo(giorno);
    sostituisci(riepilogo);
    sostituisci(turni);
    sostituisci(griglia, h('p', { classe: 'vuota' }, t('agenda.inCarico')));
    stopGiorno = ascoltaGiornoIntero(ctx, giorno, (x) => {
      ultimo = x;
      stato.textContent = x.dallaCache && !navigator.onLine ? t('rete.no') : '';
      disegna();
    }, (e) => ctx.scatola.registra(e, { dove: 'agenda' }));
  }

  // Nomi delle operatrici: scritti nell'app dalla responsabile (campo «operatrici» della struttura), mai nel codice.
  const stopStruttura = onSnapshot(doc(ctx.db, `strutture/${ctx.sid}`), (d) => {
    nomi = (d.exists() && d.data().operatrici) || {};
    disegna();
  }, (e) => ctx.scatola.registra(e, { dove: 'struttura' }));

  sostituisci(app(), h('main', { classe: 'pagina' },
    testata(t('agenda.titolo'),
      prenota ? h('button', { type: 'button', classe: 'pieno piccolo', id: 'prenota', onclick: () => apriModulo(vista, { data: giorno }) }, t('agenda.prenota')) : null),
    richieste,
    h('nav', { classe: 'giorni' },
      h('button', { type: 'button', classe: 'tondo', id: 'prima', 'aria-label': t('agenda.prima'), onclick: () => apriGiorno(spostaGiorno(giorno, -1)) }, '‹'),
      h('div', { classe: 'quale' }, titolo,
        h('button', { type: 'button', id: 'oggi', onclick: () => apriGiorno(oggiRoma()) }, t('agenda.oggi'))),
      h('button', { type: 'button', classe: 'tondo', id: 'dopo', 'aria-label': t('agenda.dopo'), onclick: () => apriGiorno(spostaGiorno(giorno, 1)) }, '›')),
    stato, riepilogo, turni, scelte, griglia));
  const stopRichieste = admin ? pannelloRichieste(ctx, richieste) : () => {};
  // La linea dell'ora attuale si sposta da sola: ridisegno ogni minuto (solo se si vede oggi).
  const orologio = setInterval(() => { if (ultimo && giorno === adessoRoma().data) disegna(); }, 60_000);
  apriGiorno(giorno);
  return () => { clearInterval(orologio); chiudiFoglio(); stopGiorno(); stopRichieste(); stopStruttura(); };
}
